import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, Grid, Typography } from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import AcxDataGrid from "components/UI/AcxDataGrid/AcxDataGrid";
import AcxMainTextField from "components/UI/AcxMainTextField";
import AcxToast from "components/UI/AcxToast";
import AcxDateRangeInput from "components/UI/Calendar/DateRange/AcxDateRangeInput";
import { observer } from "mobx-react";
import { Moment } from "moment";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "utils/useStore";
import {
    INSIGHT_ITEM_GROUP,
    LoadingAllMontages,
    MONTAGE_ITEM_GROUP,
    LEGACY_ITEM_GROUP,
    REPORTS_ITEM_GROUP,
    TEXT_ITEM_GROUP,
} from "./SignalsCardSelectorStore";
import SignalsCardSelectorStore from "./SignalsCardSelectorStore";
import { MAX_DASHBOARD_CHARTS } from "../chartLimit";
import { SignalDataEndpoint, SignalsObjectType } from "../../SignalsModels";
import type { InsightType } from "./SignalsCardSelectorStore";
import SignalsPageStore from "../../Store/SignalsPageStore";
import SmallCard from "../../BaseObjects/SmallCard/SmallCard";
import SmallCardStore from "../../BaseObjects/SmallCard/SmallCardStore";
import TextCardEditor from "../../BaseObjects/TextCard/TextCardEditor";
import ChartOption from "./ChartOption";
import QuantityPill from "./QuantityPill";
import { isLegacyStatus } from "components/UI/StatusTags";
import { AuthStore } from "stores/AuthStore";
import { getEndpointBehaviorConfig } from "components/Signals/BaseObjects/_DataEndpointConfig";
import type { ISignalsChartDefinition } from "../../SignalsModels";

const LeftItem = styled(Grid)(({ theme }) => ({
    paddingLeft: "8px",
    paddingTop: "4px",
    paddingBottom: "4px",
    paddingRight: "8px",
    borderRadius: theme.shape.borderRadius,
    cursor: "pointer",
    fontSize: "12px",
    fontWeight: 600,
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    justifyContent: "space-between",
}));

function buildPreviewSmallCardStore(
    insight: InsightType,
    rootStore: SignalsCardSelectorStore["rootStore"],
) {
    return new SmallCardStore({
        baseObject: {
            id: "",
            title: insight.presentationTitle || insight.title,
            dataEndpoint: insight.dataEndpoint as
                | SignalDataEndpoint
                | undefined,
            objectType: SignalsObjectType.SmallCard,
            order: 0,
            availableOptionObjects: insight.options,
        },
        rootStore,
        initialData: insight.dummyInfo,
        refreshOnCreate: false,
    });
}

const AddCardsDialogContent: React.FC = observer(() => {
    const theme = useTheme();
    const store = useStore(SignalsCardSelectorStore);
    const signalsPageStore = useStore(SignalsPageStore);
    const authStore = useStore(AuthStore);

    const [reportsSearchTerm, setReportsSearchTerm] = useState("");
    const [insightsSearchTerm, setInsightsSearchTerm] = useState("");

    const currentDashboard = signalsPageStore.currentDashboard;
    const existingChartCount = currentDashboard?.signalsChartCount ?? 0;
    const chartLimitReached =
        store.selectedReportCards.length + existingChartCount >=
        MAX_DASHBOARD_CHARTS;

    const matchingCharts = useMemo(() => {
        const term = reportsSearchTerm.toLowerCase();
        return store.availableCharts.filter(
            (chart) =>
                chart.title?.toLowerCase().includes(term) ||
                chart.dataEndpoint?.toLowerCase().includes(term),
        );
    }, [store.availableCharts, reportsSearchTerm]);

    // Reports split on the same rule that decides whether a Deprecated tag renders, so a report
    // never sits under Legacy Reports without the tag saying why — and a viewer who cannot see the
    // tag keeps every report where it has always been.
    const canView = useCallback(
        (resource: string) => authStore.canUserView(resource),
        [authStore],
    );
    const isEndpointLegacy = useCallback(
        (dataEndpoint?: string) =>
            isLegacyStatus(
                dataEndpoint
                    ? getEndpointBehaviorConfig({ dataEndpoint }).status
                    : undefined,
                canView,
            ),
        [canView],
    );
    const isChartLegacy = useCallback(
        (chart: ISignalsChartDefinition) =>
            isEndpointLegacy(chart.dataEndpoint),
        [isEndpointLegacy],
    );
    const filteredCharts = useMemo(
        () => matchingCharts.filter((chart) => !isChartLegacy(chart)),
        [matchingCharts, isChartLegacy],
    );
    const legacyCharts = useMemo(
        () => matchingCharts.filter(isChartLegacy),
        [matchingCharts, isChartLegacy],
    );

    const filteredInsights = useMemo(() => {
        return store.insightTypes.filter(
            (type) =>
                type.title
                    .toLowerCase()
                    .includes(insightsSearchTerm.toLowerCase()) ||
                type.presentationTitle
                    ?.toLowerCase()
                    .includes(insightsSearchTerm.toLowerCase()),
        );
    }, [store.insightTypes, insightsSearchTerm]);

    const previewSmallCardStores = useMemo(() => {
        const storesByEndpoint = new Map<string, SmallCardStore>();
        store.insightTypes.forEach((insight) => {
            storesByEndpoint.set(
                insight.dataEndpoint,
                buildPreviewSmallCardStore(insight, store.rootStore),
            );
        });
        return storesByEndpoint;
    }, [store.insightTypes, store.rootStore]);

    const montageIsLoading = !!store.getTaskLoadingV2(LoadingAllMontages);

    useEffect(() => {
        store.montageDgStore.isLoading = montageIsLoading;
    }, [store, montageIsLoading]);

    // Selected legacy reports count against Legacy Reports, not Reports, so each sidebar entry
    // reports what its own view holds.
    const selectedLegacyCount = store.selectedReportCards.filter((card) =>
        isEndpointLegacy(
            typeof card.info === "object" && card.info !== null
                ? card.info.dataEndpoint
                : undefined,
        ),
    ).length;

    const countsByGroup: Record<string, number> = {
        [REPORTS_ITEM_GROUP]:
            store.selectedReportCards.length - selectedLegacyCount,
        [INSIGHT_ITEM_GROUP]: store.selectedInsights.length,
        [TEXT_ITEM_GROUP]: store.selectedTextCards.length,
        [MONTAGE_ITEM_GROUP]: store.selectedMontages.length,
        [LEGACY_ITEM_GROUP]: selectedLegacyCount,
    };

    const CountChip = ({ value }: { value: number }) => {
        if (!value) return null;
        return (
            <span
                style={{
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    flexShrink: 0,
                    boxSizing: "border-box",
                    minWidth: "18px",
                    height: "18px",
                    padding: `0 ${theme.spacing(0.75)}`,
                    backgroundColor: theme.palette.primary.main,
                    color: theme.palette.white.main,
                    borderRadius: theme.shape.borderRadius,
                    fontSize: "10px",
                    lineHeight: 1,
                }}
            >
                {value}
            </span>
        );
    };

    const reportsView = (
        <>
            <Grid
                item
                xs={12}
                style={{ marginBottom: "8px", maxHeight: "26px" }}
            >
                <AcxMainTextField
                    id="signals-report-selector-search-input"
                    placeholderText="Search reports..."
                    fullWidth
                    value={reportsSearchTerm}
                    onChange={(e) => {
                        setReportsSearchTerm(e.target.value);
                    }}
                    startAdornment={<SearchIcon color="disabled" />}
                />
            </Grid>
            {chartLimitReached && (
                <Grid item xs={12} paddingBottom={theme.spacing(1)}>
                    <AcxToast
                        severity="infoV2"
                        message={`Up to ${MAX_DASHBOARD_CHARTS} reports can be added to your dashboard at one time. Please remove a report if you'd like to add another.`}
                        title={""}
                        fullWidth
                        fullWidthMessage
                    />
                </Grid>
            )}
            <Grid container justifyContent={"center"}>
                {filteredCharts.map((chart) => (
                    <ChartOption
                        chart={chart}
                        key={chart.id}
                        chartLimitReached={chartLimitReached}
                    />
                ))}
                {filteredCharts.length === 0 && (
                    <Typography
                        sx={{
                            width: "100%",
                            textAlign: "center",
                            color: theme.palette.gray.main,
                        }}
                    >
                        No charts available.
                    </Typography>
                )}
            </Grid>
        </>
    );

    // V1 surfaces only the first text card; V2 dialog supports multiples but keeps them in the shared store.
    const textCardView = (
        <TextCardEditor
            showToolbar={store.selectedItemGroup === TEXT_ITEM_GROUP}
            text={(store.selectedTextCards[0]?.info as string) || ""}
            setText={(input) => {
                store.setTextCardContent(input);
            }}
            showClear
        />
    );

    const smallCardView = (
        <Grid item xs={12}>
            <AcxMainTextField
                id="signals-insights-selector-search-input"
                placeholderText="Search cards..."
                fullWidth
                value={insightsSearchTerm}
                onChange={(e) => {
                    setInsightsSearchTerm(e.target.value);
                }}
                startAdornment={<SearchIcon color="disabled" />}
            />
            <Box
                sx={{
                    display: "grid",
                    gap: 2,
                    p: 2,
                    width: "100%",
                    boxSizing: "border-box",
                    gridTemplateColumns: {
                        xs: "1fr",
                        md: "repeat(3, minmax(0, 1fr))",
                    },
                }}
            >
                {filteredInsights.map((type) => {
                    const insightCount = store.getInsightCount(
                        type.dataEndpoint,
                    );
                    const isSelected = insightCount > 0;
                    const previewSmallCardStore = previewSmallCardStores.get(
                        type.dataEndpoint,
                    );
                    if (!previewSmallCardStore) return null;

                    const handleCardClick = () => {
                        if (isSelected) {
                            store.removeAllSelectedInsights(type.dataEndpoint);
                        } else {
                            store.addSelectedInsight({
                                insight: type.dataEndpoint,
                            });
                        }
                    };
                    return (
                        <Button
                            sx={{
                                alignItems: "stretch",
                                border: `1px solid ${theme.palette.gray[300]}`,
                                height: "100%",
                                justifyContent: "stretch",
                                minWidth: 0,
                                padding: 0,
                                width: "100%",
                                ":hover": {
                                    backgroundColor: "transparent",
                                    borderColor: theme.palette.gray[500],
                                },
                                ...(isSelected && {
                                    borderColor: theme.palette.primary[500],
                                    "&:hover": {
                                        borderColor: theme.palette.primary[500],
                                    },
                                }),
                            }}
                            disableRipple
                            key={type.dataEndpoint}
                            onClick={handleCardClick}
                        >
                            <Box sx={{ position: "relative", width: "100%" }}>
                                {isSelected && (
                                    <QuantityPill
                                        count={insightCount}
                                        onIncrement={() =>
                                            store.addSelectedInsight({
                                                insight: type.dataEndpoint,
                                            })
                                        }
                                        onDecrement={() => {
                                            if (insightCount <= 1) {
                                                store.removeAllSelectedInsights(
                                                    type.dataEndpoint,
                                                );
                                                return;
                                            }
                                            store.removeSelectedInsight(
                                                type.dataEndpoint,
                                            );
                                        }}
                                    />
                                )}
                                <SmallCard
                                    store={previewSmallCardStore}
                                    hideMenu
                                    readOnly
                                />
                            </Box>
                        </Button>
                    );
                })}
                {filteredInsights.length === 0 &&
                    store.insightTypes.length > 0 && (
                        <Typography
                            sx={{
                                width: "100%",
                                textAlign: "center",
                                color: theme.palette.gray.main,
                            }}
                        >
                            No insights found that match "{insightsSearchTerm}"
                        </Typography>
                    )}
                {store.insightTypes.length === 0 && (
                    <Typography
                        sx={{
                            width: "100%",
                            textAlign: "center",
                            color: theme.palette.gray.main,
                        }}
                    >
                        All available insights have already been added to your
                        dashboard.
                    </Typography>
                )}
            </Box>
        </Grid>
    );

    const montageDatePicker = (
        <Grid item container justifyContent="flex-end">
            <AcxDateRangeInput
                defaultStartDate={store.datePickerStore.beginDate}
                defaultEndDate={store.datePickerStore.endDate}
                onSelect={(start: Moment, end: Moment) => {
                    store.datePickerStore.setBeginDate(start);
                    store.datePickerStore.setEndDate(end);
                }}
                datePickerStore={store.datePickerStore}
            />
        </Grid>
    );

    const legacyReportsView = (
        <>
            <Grid
                item
                xs={12}
                style={{ marginBottom: "8px", maxHeight: "26px" }}
            >
                <AcxMainTextField
                    id="signals-legacy-report-selector-search-input"
                    placeholderText="Search legacy reports..."
                    fullWidth
                    value={reportsSearchTerm}
                    onChange={(e) => {
                        setReportsSearchTerm(e.target.value);
                    }}
                    startAdornment={<SearchIcon color="disabled" />}
                />
            </Grid>
            <Grid container justifyContent={"center"}>
                {legacyCharts.map((chart) => (
                    <ChartOption
                        chart={chart}
                        key={chart.id}
                        chartLimitReached={chartLimitReached}
                    />
                ))}
            </Grid>
        </>
    );

    const mainContent = (itemGroup: string) => {
        switch (itemGroup) {
            case REPORTS_ITEM_GROUP:
                return reportsView;
            case LEGACY_ITEM_GROUP:
                return legacyReportsView;
            case TEXT_ITEM_GROUP:
                return textCardView;
            case INSIGHT_ITEM_GROUP:
                return smallCardView;
            case MONTAGE_ITEM_GROUP:
                return null;
            default:
                return null;
        }
    };

    const montageGrid =
        store.selectedItemGroup === MONTAGE_ITEM_GROUP ? (
            <Grid container item height={"100%"}>
                <AcxDataGrid dataGridStore={store.montageDgStore}>
                    {montageDatePicker}
                </AcxDataGrid>
            </Grid>
        ) : null;

    const sideNav = (
        <Grid
            container
            item
            direction={"column"}
            xs={3}
            wrap="nowrap"
            sx={{
                border: "1px solid",
                borderRight: 0,
                borderColor: theme.palette.lightgrayBorder.main,
                padding: "8px",
                borderTopLeftRadius: 1,
                borderBottomLeftRadius: 1,
                height: "60vh",
                overflow: "auto",
            }}
        >
            <LeftItem
                margin="4px"
                item
                onClick={() => {
                    store.selectedItemGroup = REPORTS_ITEM_GROUP;
                }}
                style={{
                    backgroundColor:
                        store.selectedItemGroup === REPORTS_ITEM_GROUP
                            ? theme.palette.primary[50]
                            : theme.palette.white.main,
                }}
            >
                Reports <CountChip value={countsByGroup[REPORTS_ITEM_GROUP]} />
            </LeftItem>
            <LeftItem
                margin="4px"
                item
                onClick={() => {
                    store.selectedItemGroup = INSIGHT_ITEM_GROUP;
                }}
                style={{
                    backgroundColor:
                        store.selectedItemGroup === INSIGHT_ITEM_GROUP
                            ? theme.palette.primary[50]
                            : theme.palette.white.main,
                }}
            >
                Insights <CountChip value={countsByGroup[INSIGHT_ITEM_GROUP]} />
            </LeftItem>
            <LeftItem
                margin="4px"
                item
                onClick={() => {
                    store.selectedItemGroup = TEXT_ITEM_GROUP;
                }}
                style={{
                    backgroundColor:
                        store.selectedItemGroup === TEXT_ITEM_GROUP
                            ? theme.palette.primary[50]
                            : theme.palette.white.main,
                }}
            >
                Custom Text <CountChip value={countsByGroup[TEXT_ITEM_GROUP]} />
            </LeftItem>
            <LeftItem
                margin="4px"
                item
                onClick={() => {
                    store.selectedItemGroup = MONTAGE_ITEM_GROUP;
                }}
                style={{
                    backgroundColor:
                        store.selectedItemGroup === MONTAGE_ITEM_GROUP
                            ? theme.palette.primary[50]
                            : theme.palette.white.main,
                }}
                id="add-montage-section"
            >
                Montages <CountChip value={countsByGroup[MONTAGE_ITEM_GROUP]} />
            </LeftItem>
            {legacyCharts.length > 0 && (
                <LeftItem
                    margin="4px"
                    item
                    onClick={() => {
                        store.selectedItemGroup = LEGACY_ITEM_GROUP;
                    }}
                    style={{
                        backgroundColor:
                            store.selectedItemGroup === LEGACY_ITEM_GROUP
                                ? theme.palette.primary[50]
                                : theme.palette.white.main,
                    }}
                    id="add-legacy-reports-section"
                >
                    Legacy Reports{" "}
                    <CountChip value={countsByGroup[LEGACY_ITEM_GROUP]} />
                </LeftItem>
            )}
        </Grid>
    );

    return (
        <Grid container style={{ width: "80vw" }}>
            <Grid container item xs={12}>
                {sideNav}
                <Grid
                    container
                    item
                    direction={"column"}
                    wrap={"nowrap"}
                    xs={9}
                    sx={{
                        border: "1px solid",
                        borderColor: theme.palette.lightgrayBorder.main,
                        padding: 1,
                        borderTopRightRadius: 1,
                        borderBottomRightRadius: 1,
                        height: "60vh",
                        overflow: "auto",
                    }}
                >
                    {mainContent(store.selectedItemGroup)}
                    {montageGrid}
                </Grid>
            </Grid>
        </Grid>
    );
});

export default AddCardsDialogContent;
